import { useMemo, useState } from 'react'
import { object, string, InferType, array, boolean } from 'yup'
import { useFormContext } from '../context/FormContext'
import { Box, FormHelperText, InputLabel, MenuItem, TextField, FormControl, FormGroup, FormControlLabel, Checkbox } from '@mui/material'
import Select from '@mui/material/Select'

export const skillsSchema = object({
    relevantSkills: array().of(string()),
    otherRelevantSkills: string(),
    medicalBackground: string(),
    dataprotection: boolean().oneOf([true], "Bitte stimme dem Speichern deiner Daten zu.")
})

export const skillsSchema_en = object({
    relevantSkills: array().of(string()),
    otherRelevantSkills: string(),
    medicalBackground: string(),
    dataprotection: boolean().oneOf([true], "The terms and conditions must be accepted.")
})

const skillOptions = {
    'Cooking': {
        label_en: 'Cooking',
        label_de: 'Kochen',
    },
    'Welding': {
        label_en: 'Welding',
        label_de: 'Schweißen',
    },
    'Logistics': {
        label_en: 'Logistics',
        label_de: 'Logistik',
    },
    'Translation': {
        label_en: 'Translation',
        label_de: 'Übersetzen',
    },
    'Craftsmanship': {
        label_en: 'Craftsmanship',
        label_de: 'Handwerkliches Geschick',
    },
    'Sailing': {
        label_en: 'Sailing',
        label_de: 'Segeln',
    },
}

export interface SkillsData extends InferType<typeof skillsSchema> { }

const SkillsScreen = () => {

    const [multiSelectOpen, setMultiSelectOpen] = useState(false)

    const {
        formData, formik, labels, lang
    } = useFormContext()

    const { relevantSkills, otherRelevantSkills, medicalBackground, dataprotection } = formData

    const options = useMemo(() => {
        return Object.keys(skillOptions).map((key) => {
            return {
                key: key,
                label: lang === 'de' ? skillOptions[key as keyof typeof skillOptions].label_de : skillOptions[key as keyof typeof skillOptions].label_en
            }
        })
    }, [lang])

    if (!formik) {
        return null
    }

    const { handleChange, handleBlur, touched, errors, setFieldValue } = formik

    return (
        <Box width="100%" className="screen-skills" sx={{ display: 'flex', flexDirection: 'column' }}>
            <div className="react-form-title">
                <h2>{labels.skills.title}</h2>
            </div>
            <FormControl sx={{ height: '70px', marginBottom: '6px', width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)' } }} variant="standard">
                <InputLabel id="relevantSkills-label"
                >{labels.skills.relevantSkills}</InputLabel>
                <Select
                    id="relevantSkills"
                    label={labels.skills.relevantSkills}
                    labelId="relevantSkills-label"
                    value={relevantSkills}
                    onBlur={handleBlur}
                    onChange={(e) => {
                        const data = e.target.value as (string | undefined)[]
                        const filteredData = data.filter((value) => value !== undefined)
                        setFieldValue('relevantSkills', filteredData)

                    }}
                    error={touched.relevantSkills && Boolean(errors.relevantSkills)}
                    name='relevantSkills'
                    open={multiSelectOpen}
                    onOpen={() => setMultiSelectOpen(true)}
                    onClose={() => setMultiSelectOpen(false)}
                    variant="standard"
                    multiple
                    fullWidth
                    sx={{ paddingTop: '6px', '.MuiSelect-select': { display: 'flex', flexDirection: 'column', justifyContent: 'center' } }}
                    native={false}
                    renderValue={(selected) => <>{selected.map(s => s && options?.find(p => p.key === s)?.label).join(', ')}</>}
                >
                    <MenuItem
                        value={undefined}
                        sx={{ fontSize: '16px' }}
                        onClick={(e) => {
                            e.stopPropagation()
                            setMultiSelectOpen(false)
                        }}
                    >
                        {labels.general.close}
                    </MenuItem>

                    {options.map((skill) => {
                        return (<MenuItem
                            key={skill.key}
                            value={skill.key}
                            sx={{ fontSize: '16px' }}
                        >
                            <Checkbox checked={Boolean(relevantSkills?.find(value => value === skill.key))} />
                            {skill.label}
                        </MenuItem>)
                    })}
                </Select>
                <FormHelperText sx={{ mx: 0, transform: 'unset', color: 'var(--error)' }} >{touched.relevantSkills && errors.relevantSkills}</FormHelperText>
            </FormControl>

            <TextField
                id="otherRelevantskills"
                label={labels.skills.otherRelevantSkills}
                value={otherRelevantSkills}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                name='otherRelevantSkills'
                variant="standard"
                sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)', marginTop: '10px' } }}
            />
            <TextField
                id="medicalBackground"
                inputProps={{ maxLength: 255 }}
                label={labels.skills.medicalBackground}
                value={medicalBackground}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.medicalBackground && Boolean(errors.medicalBackground)}
                helperText={touched.medicalBackground && errors.medicalBackground}

                name='medicalBackground'
                variant="standard"
                sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)' } }}
            />
            <FormHelperText sx={{ fontSize: '13px', lineHeight: '21px', letterSpacing: '0.169px', mt: '-10px' }}>
                {labels.skills.medicalBackgroundPlaceholder}
            </FormHelperText>

            <FormControl sx={{ mt: '20px' }}>
                <FormGroup>
                    <FormControlLabel
                        control={<Checkbox checked={dataprotection} onChange={handleChange} name="dataprotection" />}
                        label={labels.skills.dataprotection}
                    />
                    <FormHelperText sx={{ color: 'var(--error)', mb: '10px', ml: 0, height: 0 }}>
                        {touched.dataprotection && (errors.dataprotection || ' ')}
                    </FormHelperText>
                </FormGroup>
            </FormControl>
        </Box>
    )
}

export default SkillsScreen